// Global configuration variables
export const BACKEND_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    REFRESH: '/auth/refresh',
    LOGOUT: '/auth/logout',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    SETUP_PASSWORD: '/auth/setup-password',
    SESSIONS: '/auth/sessions',
    UPDATE_PROFILE: '/auth/profile',
    CHANGE_PASSWORD: '/auth/change-password',
  },

  // Calendar
  CALENDAR: {
    BASE: '/calender',
    EVENTS: (provider) => `/calender/events/${provider}`,
    EVENT_BY_ID: (provider, id) => `/calender/events/${provider}/${id}`,
    EVENT_SIMPLE_UPDATE: (provider, id) => `/calender/events/${provider}/${id}`,
    CONTACTS: '/contacts',
  },

  // Documents
  DOCUMENTS: {
    BASE: '/documents',
    UPLOAD: '/documents/upload',
    UPLOAD_REDACTED: '/documents/upload-redacted',
    QUERY: '/documents/query',
    PINECONE_FILES: '/documents/pinecone-files',
    TAGS: '/documents/tags',
    BY_ID: (id) => `/documents/${id}`,
    STATUS: (id) => `/documents/${id}/status`,
    SUMMARY_STATUS: (id) => `/documents/${id}/summary-status`,
    DOWNLOAD_TOKEN: (id) => `/documents/${id}/download-token`,
    DOWNLOAD: (token) => `/documents/download/${token}`,
    SIGNED_URL: (id) => `/documents/${id}/signed-url`,
    DOWNLOAD_FOR_REDACTION: (id) => `/documents/${id}/download`,
    FOR_REDACTION: (id) => `/documents/${id}/for-redaction`,
    DETECTED_REDACTIONS: (id) => `/documents/${id}/detected-redactions`,
    REDACTION_CONFIG: '/documents/redaction-config',
  },

  // Email
  EMAIL: {
    BASE: '/email',
    STATUS: '/email/status',
    EMAILS: '/email/emails',
    SEND: '/email/send',
    DISCONNECT: '/email/disconnect',
    LABELS: '/email/labels',
    DELETE_LABEL: '/email/delete-label',
    BY_ID: (id) => `/email/emails/${id}`,
    MARK_READ: (id) => `/email/emails/${id}/read`,
    MARK_UNREAD: (id) => `/email/emails/${id}/unread`,
    ACTION: '/email/action',
    ATTACHMENT: (provider, messageId) =>
      `/email/attachment/${provider}/${messageId}`,
    // Draft endpoints
    DRAFT: '/email/drafts',
    DRAFT_BY_ID: (id) => `/email/drafts/${id}`,
    AUTH: {
      GMAIL: '/email/auth/gmail',
      OUTLOOK: '/email/auth/outlook',
      CALLBACK: (provider) => `/email/auth/${provider}/callback`,
    },
    AI_THREAD_RESPONSE: '/email/thread-response',
    ATTACHMENT_BY_ID: (attachmentId, messageId) =>
      `/email/single-attachment/${messageId}/${attachmentId}`,
  },

  // Organizations
  ORGANIZATIONS: {
    BASE: '/organisations',
    USERS: '/organisations/users',
    CREATE_WITH_OWNER: '/organisations/create-with-owner',
    BY_ID: (id) => `/organisations/${id}`,
    USER_BY_ID: (userId) => `/organisations/users/${userId}`,
  },

  // Contacts
  CONTACTS: {
    BASE: '/contacts',
    CATEGORIES: '/contacts/categories',
    SYNC: '/contacts/sync',
    SYNC_PUSH: '/contacts/sync/push',
    SYNC_PUSH_MICROSOFT: '/contacts/sync/push/microsoft',
    SYNC_STATUS: '/contacts/sync/status',
    SYNC_HISTORY: '/contacts/sync/history',
    STATUS: '/contacts/status',
    RECLASSIFY: '/contacts/reclassify',
    DOMAINS: '/contacts/domains',
    BY_ID: (id) => `/contacts/${id}`,
    CATEGORY: (id) => `/contacts/${id}/category`,
    GET_CONTACT_BY_EMAIL: '/contacts/contact-by-email',
    DOMAIN_BY_ID: (id) => `/contacts/domains/${id}`,
    COPY_TO_REGULAR: (id) => `/contacts/${id}/copy-to-regular`,
    USER_CONTACTS: '/contacts/user-contacts',
    // Notes endpoints
    NOTES: (contactId) => `/contacts/${contactId}/notes`,
    NOTE_BY_ID: (contactId, noteId) => `/contacts/${contactId}/notes/${noteId}`,
    // OAuth endpoints
    OAUTH: {
      GOOGLE: {
        URL: '/contacts/oauth/google/url',
        EXCHANGE: '/contacts/oauth/google/exchange',
        STATUS: '/contacts/oauth/google/status',
        HEALTH: '/contacts/oauth/google/health',
        CALLBACK: '/contacts/oauth/google/callback',
        DISCONNECT: '/contacts/oauth/google/disconnect',
      },
      MICROSOFT: {
        URL: '/contacts/oauth/microsoft/url',
        EXCHANGE: '/contacts/oauth/microsoft/exchange',
        STATUS: '/contacts/oauth/microsoft/status',
        HEALTH: '/contacts/oauth/microsoft/health',
        CALLBACK: '/contacts/oauth/microsoft/callback',
        DISCONNECT: '/contacts/oauth/microsoft/disconnect',
      },
    },
  },

  // Marketplace
  MARKETPLACE: {
    BASE: '/marketplace-app',
    ORGANIZATION_REDACTION: '/marketplace-app/redaction',

    // SuperAdmin endpoints
    CREATE_APP: '/marketplace-app',
    GET_ALL_APPS: '/marketplace-app',
    UPDATE_APP: (id) => `/marketplace-app/${id}`, // Handles both update and visibility toggle
    SEARCH_APP: (searchText) => `/marketplace-app?searchText=${searchText}`, // Handles both update and visibility toggle
    SEARCH_APP_MAPPING: (searchText) =>
      `/marketplace-app-mapping?searchText=${searchText}`, // Handles both update and visibility toggle

    // Owner/Admin endpoints
    GET_APPS_WITH_MAPPING: '/marketplace-app-mapping',
    ENABLE_DISABLE_APP: '/marketplace-app-mapping',
  },

  // Inmate Onboarding Form
  INMATE_ONBOARDING_FORM: {
    GET_FORM_DATA: 'form/data',
    GET_FORM_DATA_ACTIVE: 'form/data/active',
    SAVE_INMATE_RESPONSES: 'form/inmate-onboarding/save',
    GET_INMATE_RESPONSES: (inmateId) => `form/responses/inmate/${inmateId}`,
    CREATE_SECTION: 'form/sections',
    UPDATE_SECTION: (sectionId) => `form/sections/${sectionId}`,
    BULK_UPDATE_SECTIONS: 'form/sections/bulk-update',
  },

  // Resource Applications (Organization Application Forms)
  RESOURCE_APPLICATIONS: {
    // Form endpoints
    GET_FORM: 'resource-applications/forms',
    UPDATE_FORM: (formId) => `resource-applications/forms/${formId}`,
    // Section endpoints
    CREATE_OR_UPDATE_SECTION: 'resource-applications/sections',
    UPDATE_SECTION: (sectionId) => `resource-applications/sections/${sectionId}`,
    DELETE_SECTION: (sectionId) => `resource-applications/sections/${sectionId}`,
    // Legacy endpoints (for backward compatibility)
    GET_SECTIONS: 'resource-applications/sections',
    BULK_UPDATE_SECTIONS: 'resource-applications/bulk-update-sections',
    GET_DATA: 'resource-applications/data',
    GET_ACTIVE_DATA: 'resource-applications/data/active',
    GET_APPLICATION_FORM: 'resource-applications/application-form',
  },

  //fac
  FACILITY: {
    GET_FACILITIES: 'facilities',
    GET_ACTIVE_FACILITIES: 'facilities/active',
    CREATE_FACILITY: 'facilities',
    UPDATE_FACILITY: (facilityId) => `facilities/${facilityId}`,
    DELETE_FACILITY: (facilityId) => `facilities/${facilityId}`,
    GET_ROLE_NAMES: (facilityId) => `facilities/${facilityId}/role-names`,
    UPDATE_ROLE_NAMES: (facilityId) => `facilities/${facilityId}/role-names`,
  },

  // Message Rate Limits
  MESSAGE_RATE_LIMIT: {
    GET_FACILITIES: 'message-rate-limits/facilities',
    GET_FACILITY: (facilityId) => `message-rate-limits/facility/${facilityId}`,
    SET_FACILITY: (facilityId) => `message-rate-limits/facility/${facilityId}`,
  },

  //COORDINATOR
  COORDINATOR: {
    GET_COORDINATORS: 'users',
    GET_ACTIVE_COORDINATORS: 'coordinators/active',
    SEND_INVITATION: 'auth/send-invitation',
    UPDATE_COORDINATOR: (id) => `users/coordinators/${id}`,
    DELETE_COORDINATOR: (id) => `users/coordinators/${id}`,
  },

  RESOURCE_ORGANIZATIONS: {
    BASE: '/resource-organisations',
    UPDATE_ORGANIZATION: (id) => `/resource-organisations/${id}`,
    ADD_ORGANIZATION: '/resource-organisations',
    FETCH_RESOURCES: (id) => `/resource-organisations/${id}/fetch-resources`,
  },

  // Resources
  RESOURCES: {
    BASE: '/resources',
    BY_ORGANIZATION: (organizationId) =>
      `/resources/organization/${organizationId}`,
    BY_ID: (id) => `/resources/${id}`,
    TOGGLE_STATUS: (id) => `/resources/${id}/toggle-status`,
  },

  // Resource Admins
  RESOURCE_ADMINS: {
    BASE: '/resource-admins',
    BY_ID: (id) => `/resource-admins/${id}`,
  },

  // Redaction (standalone feature)
  REDACTION: {
    CONFIG: '/redaction/config',
    START: '/redaction/start',
    BY_ID: (id) => `/redaction/${id}`,
    ANALYZE: (id) => `/redaction/${id}/analyze`,
    REDACTIONS: (id) => `/redaction/${id}/redactions`,
    EXPORT: (id) => `/redaction/${id}/export`,
    CONVERT: (id) => `/redaction/${id}/convert`,
    DOWNLOAD: (id) => `/redaction/${id}/download`,
    DOWNLOAD_CONVERTED: (id) => `/redaction/${id}/converted/download`,
  },
  // Resource Applications
  ORGANIZATION_APPLICATIONS: {
    CREATE_APPLICATION_FORM: `/resource-applications/forms`,
    GET_APPLICATION_FORMS_BY_ORGANIZATION: (organizationId) =>
      `/resource-applications/forms/organization/${organizationId}`,
    GET_APPLICATION_FORM_BY_ID: (id) => `/resource-applications/forms/${id}`,
    UPDATE_APPLICATION_FORM: (id) => `/resource-applications/forms/${id}`,
    CREATE_SECTION: `/resource-applications/sections`,
  },
};
